import * as PATHS from '@/constants/path';
import { request } from '@/services/api/request';
import { FileKind, FileModel, FileUsageModel } from '@/typings/api/files';
import { URLParamBuilder } from '@/utils/functions';

/**
 * The partner file library.
 *
 * Every upload in the backoffice goes through here, so that a file always
 * exists as a record the partner owns and can find again — not just as bytes
 * somewhere in a bucket.
 *
 * `url` always comes from the response. Where a file is reachable depends on
 * which disk it was written to (the public bucket in deployed environments, the
 * app itself in development), which is the server's business, not the client's.
 */

export type FetchFilesQuery = {
  kind?: FileKind;
  context?: string;
  search?: string;
  page?: number;
  page_size?: number;
};

export function fetchFilesRequest({
  query = {},
}: { query?: FetchFilesQuery } = {}) {
  const url = URLParamBuilder(PATHS.FILES, { queryString: query });
  return request<any>(url, { method: 'GET' });
}

/**
 * `FormData`, so the browser writes the multipart boundary itself — naming
 * Content-Type by hand produces a boundary-less header the server cannot parse.
 */
export function uploadFileRequest({
  file,
  context,
  description,
}: {
  file: File | Blob;
  context?: string;
  description?: string;
}) {
  const data = new FormData();
  data.append('file', file);
  if (context) data.append('context', context);
  if (description) data.append('description', description);

  return request<any>(PATHS.FILES, {
    method: 'POST',
    // Cast: the helper types `data` as a plain record, but a multipart upload
    // is a FormData — the runtime accepts it, the signature just predates it.
    data: data as any,
  });
}

export function updateFileRequest({
  params,
  payload,
}: {
  params: { fileId: string };
  payload: Partial<Pick<FileModel, 'name' | 'description'>>;
}) {
  const url = URLParamBuilder(PATHS.FILE, { params });
  return request<any>(url, { method: 'PATCH', data: payload });
}

/**
 * Deleting is refused with 409 while something still references the file; the
 * body then carries the usages. `force` is the answer to that, and is only
 * offered after the references have been shown.
 */
export function deleteFileRequest({
  params,
  force = false,
}: {
  params: { fileId: string };
  force?: boolean;
}) {
  const url = URLParamBuilder(PATHS.FILE, {
    params,
    queryString: force ? { force: '1' } : {},
  });
  return request<any>(url, { method: 'DELETE' });
}

/** What references the file — asked before offering the delete, not after. */
export function fetchFileUsageRequest({
  params,
}: {
  params: { fileId: string };
}) {
  const url = URLParamBuilder(PATHS.FILE_USAGE, { params });
  return request<any>(url, { method: 'GET' });
}

export type { FileKind, FileModel, FileUsageModel };
